import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/Button';
import { Check, Zap, Users, Building2, Sparkles } from 'lucide-react';

export default function Pricing() {
  const { isDark } = useTheme();

  const plans = [
    {
      name: "Starter",
      price: "199",
      description: "Pour les petites équipes jusqu'à 5 agents",
      features: [
        "Jusqu'à 5 agents",
        "Timer intelligent",
        "KPI temps réel",
        "Tracking appels",
        "Export Excel",
        "Support email"
      ],
      icon: Zap,
      popular: false
    },
    {
      name: "Pro",
      price: "399",
      description: "Pour les équipes en croissance",
      features: [
        "Jusqu'à 20 agents",
        "Tout du Starter +",
        "Leaderboard performance",
        "Commissions automatiques",
        "Supervision live",
        "Support prioritaire"
      ],
      icon: Users,
      popular: true
    },
    {
      name: "Enterprise",
      price: "799",
      description: "Pour les grandes structures",
      features: [
        "Agents illimités",
        "Tout du Pro +",
        "API dédiée",
        "SLA personnalisé",
        "Comptes multiples",
        "Support dédié 24/7"
      ],
      icon: Building2,
      popular: false
    }
  ];

  return (
    <section id="pricing" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
            <Sparkles className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
              Pricing simple et transparent
            </span>
          </div>
          <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Des tarifs adaptés à vos besoins
          </h2>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Pas de frais cachés, pas de surprise
          </p>
        </div>

        {/* Grille des prix */}
        <div className="grid lg:grid-cols-3 gap-8">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            const isPopular = plan.popular;

            return (
              <div
                key={index}
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${
                  isPopular
                    ? isDark
                      ? 'bg-gradient-to-b from-primary-900/50 to-purple-900/50 border-2 border-primary-500 scale-105 shadow-xl shadow-primary-500/20'
                      : 'bg-gradient-to-b from-primary-50 to-purple-50 border-2 border-primary-500 scale-105 shadow-xl shadow-primary-500/20'
                    : isDark
                      ? 'bg-gray-900 border border-gray-800'
                      : 'bg-white border border-gray-200'
                }`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-primary-600 text-white px-4 py-1 text-sm font-medium rounded-bl-xl">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="p-8">
                  <div className={`w-12 h-12 rounded-xl ${
                    isPopular
                      ? 'bg-primary-600'
                      : isDark
                        ? 'bg-gray-800'
                        : 'bg-gray-100'
                  } flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${
                      isPopular ? 'text-white' : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`} />
                  </div>

                  <h3 className={`text-2xl font-bold mb-2 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {plan.description}
                  </p>
                  
                  <div className="mb-6">
                    <span className={`text-4xl font-bold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {plan.price}€
                    </span>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      /mois
                    </span>
                  </div>

                  <Button
                    variant={isPopular ? 'primary' : 'outline'}
                    fullWidth
                    size="lg"
                    className="mb-8"
                  >
                    Choisir {plan.name}
                  </Button>

                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <Check className={`w-5 h-5 ${
                          isPopular ? 'text-primary-600' : 'text-emerald-600'
                        }`} />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Note */}
        <p className={`text-center text-sm mt-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Tous les plans incluent la synchronisation temps réel Supabase
        </p>
      </div>
    </section>
  );
}